// Voucher presentation helpers.

import { formatAmount } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import type { Voucher } from './types';

/**
 * Human-readable value: "25% off" or "500.00 off" (symbol per currency).
 *
 * PERCENTAGE: `value` is the percent — rendered verbatim (unchanged).
 * FLAT: `value` is in the smallest unit (paise for INR). The money branch
 * delegates to the single source of truth `@bbpos/utils#formatAmount` instead of
 * the INR-hardcoded `formatPaise`; `currency` is required (no INR default,
 * CU2-001) so every caller passes the tenant currency and gets correct subunit math
 * (e.g. OMR/BHD/KWD are 3-decimal currencies). No hardcoded currency symbol or
 * `/ 100` remains.
 */
export function formatVoucherValue(
  voucher: Pick<Voucher, 'type' | 'value'>,
  currency: CurrencyCode,
): string {
  if (voucher.type === 'PERCENTAGE') {
    return `${voucher.value}% off`;
  }
  return `${formatAmount(voucher.value, currency)} off`;
}

/**
 * Days left after today. `expires_at` is the exclusive end (the instant after
 * the "Valid until" day, CU2-006), so its last day is 0. Null if no expiry.
 * Negative once expired.
 */
export function daysUntil(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24)) - 1;
}

/**
 * A voucher's first day (`starts_at`) or, with `end`, its last day
 * (`expires_at` is the exclusive end) on the store's clock (CU2-006).
 */
export function formatVoucherDay(iso: string, timeZone: string, end = false): string {
  const t = new Date(iso).getTime() - (end ? 1 : 0);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone });
}
